import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { List } from 'immutable';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import NotificationItem from 'modules/Notifications/components/NotificationItem';
import styles from './NotificationsList.module.scss';
import LoadOnScroll from 'components/LoadOnScroll';
import { ReactComponent as RemoveAllIcon } from 'images/icons-regular-icons-ic-delete.svg';
import { ReactComponent as ReadAllIcon } from 'images/icons-regular-icons-ic-done-all.svg';
import useScrollDirection from 'hooks/useScrollDirection';

const MIN_SCROLL_NOTIFICATION_COUNT = 3;
const THRESHOLD = 32;

const NotificationsList = ({
  notifications,
  onRead,
  onRemove,
  onRemoveAll,
  onReadAll,
  hasMore,
  fetchMore,
  fetching,
  onItemClick,
}) => {
  const listContainer = useRef(null);
  const listControls = useRef(null);
  const [shouldShowRemove, setShowRemove] = useState(false);

  useEffect(() => {
    if (listContainer && listContainer.current && listControls && listControls.current) {
      if (
        listContainer.current.scrollHeight - listControls.current.clientHeight >
        listContainer.current.clientHeight
      ) {
        listContainer.current.scrollTop = listControls.current.offsetHeight;
      }
    }

    if (listContainer.current.scrollHeight <= listContainer.current.clientHeight + THRESHOLD * 2) {
      setShowRemove(true);
    }
  }, []);

  const direction = useScrollDirection({ ref: listContainer, threshold: THRESHOLD });
  const showReadAll = useMemo(
    () => notifications.filter((n) => !n.get('readingDate')).size >= MIN_SCROLL_NOTIFICATION_COUNT,
    [notifications],
  );

  return (
    <div className={styles.list}>
      <LoadOnScroll
        fetchMore={fetchMore}
        fetching={fetching}
        hasMore={hasMore}
        ref={listContainer}
        className={styles.notificationScrollContainer}
      >
        {showReadAll && (
          <div ref={listControls} className={styles.controls}>
            <div className={styles.controlItem} onClick={onReadAll}>
              <ReadAllIcon />
              Прочитать все
            </div>
          </div>
        )}
        <TransitionGroup className={styles.animation}>
          {notifications.map((notification) => (
            <CSSTransition
              key={notification.get('id')}
              timeout={200}
              classNames={{
                enter: styles.enter,
                enterActive: styles.enterActive,
                exit: styles.exit,
                exitActive: styles.exitActive,
              }}
            >
              <NotificationItem
                item={notification}
                onRead={onRead}
                onRemove={onRemove}
                onClick={onItemClick}
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
      </LoadOnScroll>

      <CSSTransition
        timeout={200}
        in={
          notifications.size >= MIN_SCROLL_NOTIFICATION_COUNT &&
          (direction === 'DOWN' || shouldShowRemove)
        }
        unmountOnExit
        classNames={{
          enter: styles.btnEnter,
          enterActive: styles.btnEnterActive,
          exit: styles.btnExit,
          exitActive: styles.btnExitActive,
        }}
      >
        <div className={styles.removeAllContainer}>
          <div className={styles.removeAll} onClick={onRemoveAll}>
            <RemoveAllIcon />
            Удалить все
          </div>
        </div>
      </CSSTransition>
    </div>
  );
};

NotificationsList.propTypes = {
  notifications: PropTypes.instanceOf(List),
  onRead: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReadAll: PropTypes.func.isRequired,
  onRemoveAll: PropTypes.func.isRequired,
  fetching: PropTypes.bool,
  hasMore: PropTypes.bool,
  fetchMore: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
};

export default NotificationsList;
